import styles from './ScheduleTheme.module.scss';
import { $Flexy, Flexy } from '@webinex/flexy';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { TextBox, TableColumnType, FieldRow, formatters, Icon } from '@app/ui';
import {
  AppointmentScheduleEventModalLocationBlock,
  AppointmentScheduleEventModalStatusBlock,
  AppointmentScheduleEventModalVisitorBlock,
  AppointmentScheduleEventModalVisitorSection,
  DndCalendarWeekAgendaTable,
  AppointmentEvent,
  Event,
  CompleteAppointmentButtonPanel,
  AppointmentAgendaListItemTimeSectionContent,
  AppointmentsAgendaListItem,
  CreateAttendedAppointmentButtonPanel,
  FormHostSearch,
  FormSlotPicker,
  AppointmentSlotApplyForm,
  AppointmentSlotCalendar,
  Host,
  Visitor,
  AppointmentSummaryPanelContent,
  CancelAppointmentModal,
  CancelAppointmentModalText,
  AppointmentAgendaListItemTimeSection,
  AddAppointmentScheduleEventButton,
  AppointmentsChartModalWidget,
  AvailabilityPanel,
  AvailabilityEditButton,
  AppointmentRequestListTable,
  AppointmentRequestFormValue,
  AppointmentRequestListWidget,
  AppointmentRequestScheduleAppointmentModal,
  FormAppointmentGroupToggle,
  FormVisitorReferenceIndividual,
  FormAddAppointmentVisitor,
  AppointmentRequestDetailsModalWidget,
  AppointmentRequestDetailsWidget,
  FormAppointmentRequestRequestedPeriod,
  NoAvailableAppointmentSlotsButton,
  AppointmentRequestStatus,
  FormCareHome,
  AppointmentType,
  AppointmentTypeValue,
  useEditAppointmentEventContext,
  AppointmentSlotApplyFormContent,
  AppointmentRequestListAction,
  VisitorRefAppointmentTypeType,
  CreateAppointmentRequestButtonWidget,
  VisitorReferenceFormValue,
  FormVisitorReferenceGroup,
  AppointmentRequest,
  AddAppointmentWidget,
} from '@mod/schedule';
import { Button, Col, Popover, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  mapPatientLookupOption,
  PatientLookupOption,
  useRoleBasedAccess,
  useUserContextSelector,
} from '@app/main/utils';
import { Link, NavLink } from 'react-router-dom';
import { useRedirectToEncounterByAppointmentEvent } from '@app/medical-record/utils';
import {
  CustomFormHostSearch,
  CustomAppointmentSlotApplyForm,
  CustomAppointmentSlotApplyFormContent,
} from './CustomAppointmentSlotApplyForm';
import { EmployeeOverviewPanel } from '../Employees.Overview';
import { EncounterOverviewWidget } from '@app/medical-record/widgets/Encounters.Overview';
import { Form } from '@webinex/antik';
import classNames from 'classnames';
import { Name } from '@app/main/components';
import { CatalogCareHomeType, useGetCatalogCareHomeQuery } from '@app/medical-record/api';
import { useChangeEffect, useUrlTableState } from '@app/common/utils';
import { useField, useFormikContext } from 'formik';
import {
  CatalogCareHomeOption,
  mapCatalogCareHomeOption,
  useCatalogCareHomeOptionSource,
} from '@app/medical-record/widgets/CatalogCareHomes.Common';
import { PatientPortalAppointmentRequestDetailsWidget } from '@app/medical-record/widgets/PatientPortalAppointmentsRequests.Details';
import { PatientPortalAppointmentRequestsListWidget } from '@app/medical-record/widgets/PatientPortalAppointmentsRequests.List';
import { useGetPatientIdsByCareHomeQuery, useGetPatientLookupQuery } from '@app/main/api';
import { CustomAppointmentRequestDetailsWidget } from './CustomAppointmentRequestDetailsWidget';
import { CancelAppointmentChatModalWidget } from '@app/medical-record/widgets/Appointments.Cancel';
import { CustomAddAppointmentWidget } from './CustomAddAppointmentWidget';

type ScheduleTheme = $Flexy<
  | typeof AppointmentScheduleEventModalStatusBlock
  | typeof AppointmentScheduleEventModalVisitorBlock
  | typeof AppointmentScheduleEventModalVisitorSection
  | typeof AppointmentScheduleEventModalLocationBlock
  | typeof DndCalendarWeekAgendaTable
  | typeof CompleteAppointmentButtonPanel
  | typeof AppointmentAgendaListItemTimeSectionContent
  | typeof AppointmentsAgendaListItem
  | typeof CreateAttendedAppointmentButtonPanel
  | typeof FormHostSearch
  | typeof FormSlotPicker
  | typeof AppointmentSlotApplyForm
  | typeof AppointmentSlotCalendar
  | typeof Host
  | typeof Visitor
  | typeof AppointmentSummaryPanelContent
  | typeof CancelAppointmentModal
  | typeof CancelAppointmentModalText
  | typeof AppointmentAgendaListItemTimeSection
  | typeof AddAppointmentScheduleEventButton
  | typeof AppointmentsChartModalWidget
  | typeof AvailabilityPanel
  | typeof AvailabilityEditButton
  | typeof AppointmentRequestListTable
  | typeof FormAppointmentGroupToggle
  | typeof FormVisitorReferenceIndividual
  | typeof FormAddAppointmentVisitor
  | typeof AppointmentRequestDetailsModalWidget
  | typeof AppointmentRequestDetailsWidget
  | typeof FormAppointmentRequestRequestedPeriod
  | typeof AppointmentRequestListWidget
  | typeof AppointmentRequestScheduleAppointmentModal
  | typeof NoAvailableAppointmentSlotsButton
  | typeof FormCareHome
  | typeof AppointmentSlotApplyFormContent
  | typeof AppointmentTypeValue
  | typeof CreateAppointmentRequestButtonWidget
  | typeof FormVisitorReferenceGroup
  | typeof AddAppointmentWidget
>;

const APPOINTMENT_TYPE_CATALOG_CARE_HOME_TYPE_MAP: Record<AppointmentType, CatalogCareHomeType> = {
  Offline: 'PHYSICAL',
};

function formatAppointmentRequestVisitors(item: AppointmentRequest) {
  return item.visitorRefs?.map((visitorRef) => formatters.name(visitorRef.visitor)).join(', ') ?? '';
}

function formatAppointmentRequestTypes(item: AppointmentRequest) {
  return item.visitorRefs?.map((visitorRef) => visitorRef.appointmentType.name).join(', ') ?? '';
}

const ShowEncounterLink = (props: { encounter: AppointmentEvent['encounter']; showNotCreated?: boolean }) => {
  const { encounter, showNotCreated = true } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
  const roleAccessMap = useRoleBasedAccess();

  if (!encounter) {
    return showNotCreated ? <span>{t('encounterNotCreated')}</span> : null;
  }

  if (roleAccessMap.PatientManagementRoles) {
    return (
      <div>
        <NavLink to={`/encounter/${encounter.id}`}>{t('showEncounter')}</NavLink>
      </div>
    );
  }

  return (
    <Tooltip title={t('showEncounterHint')}>
      <Button type="link" disabled>
        {t('showEncounter')}
      </Button>
    </Tooltip>
  );
};

const SCHEDULE_THEME: ScheduleTheme = {
  Host: (props) => {
    const { value } = props;

    return (
      <Popover content={<EmployeeOverviewPanel id={value.id} />}>
        <Button type="link" className="whitespace-normal break-all">
          <Name value={value} />
        </Button>
      </Popover>
    );
  },

  Visitor: (props) => {
    const { value } = props;
    const roleAccessMap = useRoleBasedAccess();
    return roleAccessMap.PatientManagementRoles ? (
      <Link to={`/patient/${value.id}`}>
        <Name value={value} />
      </Link>
    ) : (
      <Name value={value} />
    );
  },

  AppointmentsAgendaListItem: (props) => {
    const [isPerformer] = useUserContextSelector((x) => [x.hasRole('PERFORMER')]);
    const redirectToEncounter = useRedirectToEncounterByAppointmentEvent();

    return (
      <AppointmentsAgendaListItem.Component
        {...props}
        className={classNames(props.className, styles.appointmentListItem)}
        showCompleteAsAttended={isPerformer}
        onCompletedAsAttended={redirectToEncounter}
      />
    );
  },

  AppointmentAgendaListItemTimeSection: (props) => {
    const { event } = props;
    const isEmployee = useUserContextSelector((x) => x.hasRole('PERFORMER') || x.hasRole('ADMIN'));
    const element = <AppointmentAgendaListItemTimeSection.Component {...props} />;
    const visitorAddress = event.visitor?.address ? formatters.address(event.visitor.address) : null;

    if (isEmployee) {
      return (
        <Space direction="vertical" size="small" className="text-end">
          {element}
          <div>
            <ShowEncounterLink encounter={event.encounter} showNotCreated={false} />
          </div>
          {visitorAddress && (
            <div className="max-w-[160px] text-multiline ml-auto text-description text-sm">
              <Icon type="location" className="mr-1" />
              {visitorAddress}
            </div>
          )}
        </Space>
      );
    }

    return <span className="text-end">{element}</span>;
  },

  DndCalendarWeekAgendaTable: (props) => {
    const columns = props.columns! as TableColumnType<Event<AppointmentEvent>>[];
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });

    const newColumns = useMemo(() => {
      const newColumns = [...columns];

      newColumns.splice(newColumns.length - 1, 0, {
        key: 'encounter',
        title: t('encounter'),
        render: (_, { value: { encounter } }) => <ShowEncounterLink encounter={encounter} />,
      });

      return newColumns;
    }, [columns, t]);

    return <DndCalendarWeekAgendaTable.Component {...props} columns={newColumns as any} />;
  },

  AppointmentScheduleEventModalStatusBlock: (props) => {
    const { event } = props;
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
    const encounter = event.encounter;

    return (
      <FieldRow>
        <Col span={12}>
          <AppointmentScheduleEventModalStatusBlock.Component {...props} />
        </Col>
        {encounter && (
          <Col span={12}>
            <TextBox label={t('encounter')}>
              <ShowEncounterLink encounter={encounter} />
            </TextBox>
          </Col>
        )}
      </FieldRow>
    );
  },

  AppointmentScheduleEventModalVisitorSection: (props) => {
    const { event } = props;
    const { t } = useTranslation();
    const visitor = event.visitor;
    const visitorCareHome = visitor?.careHome;

    if (visitorCareHome) {
      return (
        <>
          <FieldRow className="mb-4">
            <Col span={12}>
              <AppointmentScheduleEventModalVisitorBlock event={event} />
            </Col>
          </FieldRow>
          <AppointmentScheduleEventModalLocationBlock event={event} />
        </>
      );
    }

    return (
      <FieldRow className="mb-4">
        <Col span={12}>
          <AppointmentScheduleEventModalVisitorBlock event={event} />
        </Col>
        <Col span={12}>
          <TextBox label={t('address')}>
            {visitor?.address ? formatters.address(visitor.address) : t('notApplicable')}
          </TextBox>
        </Col>
      </FieldRow>
    );
  },

  AppointmentScheduleEventModalLocationBlock: (props) => {
    const { event } = props;
    const { t } = useTranslation();
    const visitor = event.visitor;
    const visitorCareHome = visitor?.careHome;

    if (visitorCareHome) {
      return (
        <FieldRow className="mb-4">
          <Col span={12}>
            <TextBox label={t('appointments.careHome')}>{visitorCareHome.name ?? t('notApplicable')}</TextBox>
          </Col>
          <Col span={12}>
            <TextBox label={t('address')}>
              {visitorCareHome.address ? formatters.address(visitorCareHome.address) : t('notApplicable')}
            </TextBox>
          </Col>
        </FieldRow>
      );
    }

    return (
      <FieldRow className="mb-4">
        <Col span={12}>
          <TextBox label={t('address')}>
            {visitor?.address ? formatters.address(visitor.address) : t('notApplicable')}
          </TextBox>
        </Col>
      </FieldRow>
    );
  },

  CompleteAppointmentButtonPanel: (props) => {
    const { event } = props;
    const [id, isCoordinator] = useUserContextSelector((x) => [x.id, x.hasRole('COORDINATOR')]);
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
    const disableAsCanBeCompletedByProviderOnly = event.status === 'InProgress' && event.hostId !== id;

    return (
      (isCoordinator || id === event.hostId) && (
        <CompleteAppointmentButtonPanel.Component
          {...props}
          disabled={props.disabled ?? disableAsCanBeCompletedByProviderOnly}
          tooltip={
            disableAsCanBeCompletedByProviderOnly
              ? t('encounterStartedCanBeCompleteOnlyByProviderTooltip')
              : t('completeAppointmentTooltip')
          }
        />
      )
    );
  },

  CreateAttendedAppointmentButtonPanel: (props) => {
    const redirectToEncounter = useRedirectToEncounterByAppointmentEvent();
    const id = useUserContextSelector((x) => x.id);

    return (
      <CreateAttendedAppointmentButtonPanel.Component
        {...props}
        onCreated={redirectToEncounter}
        hostId={id}
      />
    );
  },

  AppointmentSummaryPanelContent: (props) => {
    const { event, children } = props;
    const encounter = event.encounter;
    const isPerformer = useUserContextSelector((x) => x.hasRole('PERFORMER'));

    return (
      <>
        {children}
        {encounter && isPerformer && <EncounterOverviewWidget id={encounter.id} />}
      </>
    );
  },

  AddAppointmentScheduleEventButton: (props) => {
    const [isPerformer, isCoordinator] = useUserContextSelector((x) => [
      x.hasRole('PERFORMER'),
      x.hasRole('COORDINATOR'),
    ]);

    if (isPerformer || isCoordinator) return <AddAppointmentScheduleEventButton.Component {...props} />;
    return null;
  },

  AppointmentsChartModalWidget: (props) => {
    return (
      <AppointmentsChartModalWidget.Component
        {...props}
        modal={{ ...props.modal, className: 'min-w-[70%]' }}
      />
    );
  },

  AvailabilityPanel: (props) => {
    const [isAdmin] = useUserContextSelector((x) => [x.hasRole('ADMIN')]);
    return <AvailabilityPanel.Component {...props} canManage={isAdmin} />;
  },

  AppointmentRequestListWidget: (props) => {
    const [isPerformer, isCoordinator, isCareHomeStaff, isPatient, careHomeId] = useUserContextSelector(
      (x) => [
        x.hasRole('PERFORMER'),
        x.hasRole('COORDINATOR'),
        x.hasRole('CARE_HOME_STAFF'),
        x.hasRole('PATIENT'),
        x.careHomeId,
      ],
    );

    const shouldLoadCareHomePatientIds = isCareHomeStaff && !isPerformer && !isCoordinator && !!careHomeId;

    const { data: careHomePatientIds, isLoading } = useGetPatientIdsByCareHomeQuery(
      { careHomeId: careHomeId! },
      { skip: !shouldLoadCareHomePatientIds },
    );

    const filterActions = (roleActions: AppointmentRequestListAction[]) =>
      props.actions
        ? props.actions.filter((action: AppointmentRequestListAction) => roleActions.includes(action))
        : roleActions;

    const getActions = (): AppointmentRequestListAction[] => {
      if (isPerformer || isCoordinator) {
        return filterActions(['ViewDetails', 'ChangeStatus', 'ScheduleAppointment']);
      }

      if (isCareHomeStaff || isPatient) {
        return filterActions(['ViewDetails', 'Cancel']);
      }

      return filterActions(['ViewDetails']);
    };

    if (shouldLoadCareHomePatientIds && isLoading) {
      return null;
    }

    const visitorIds = shouldLoadCareHomePatientIds ? careHomePatientIds : props.visitorIds;
    const actions = getActions();

    if (isPatient) {
      return (
        <PatientPortalAppointmentRequestsListWidget {...props} actions={actions} visitorIds={visitorIds} />
      );
    }

    return <AppointmentRequestListWidget.Component {...props} actions={actions} visitorIds={visitorIds} />;
  },

  AppointmentRequestScheduleAppointmentModal: (props) => {
    const isGroup = props.item.visitorRefs.length > 1;
    const firstVisitorRef = props.item.visitorRefs[0];
    const defaultCareHome = firstVisitorRef?.visitor?.careHome;

    return (
      <AppointmentRequestScheduleAppointmentModal.Component
        {...props}
        defaultCareHomeId={isGroup ? defaultCareHome?.id : undefined}
        defaultCareHomeOption={
          isGroup && defaultCareHome ? mapCatalogCareHomeOption(defaultCareHome) : undefined
        }
      />
    );
  },

  CreateAppointmentRequestButtonWidget: (props) => {
    const [isPatient, isCareHomeStaff] = useUserContextSelector((x) => [
      x.hasRole('PATIENT'),
      x.hasRole('CARE_HOME_STAFF'),
    ]);

    if (isPatient || isCareHomeStaff) {
      return <CreateAppointmentRequestButtonWidget.Component {...props} />;
    }

    return null;
  },

  AppointmentRequestListTable: (props) => {
    const [tableState, setTableState, searchValue, onSearchChange] = useUrlTableState(
      'appointment-request-list',
      {
        sortRule: { fieldId: 'requestedPeriod.start', dir: 'asc' },
        filterRules: [{ fieldId: 'status', operator: '=', value: AppointmentRequestStatus.Active }],
      },
    );

    const columns = useMemo(() => {
      return props.columns?.map((column) => {
        if (column.key === 'visitor') {
          return {
            ...column,
            exportValue: column.exportValue ?? formatAppointmentRequestVisitors,
          };
        }

        if (column.key === 'type') {
          return {
            ...column,
            exportValue: column.exportValue ?? formatAppointmentRequestTypes,
          };
        }

        return column;
      });
    }, [props.columns]);

    return (
      <AppointmentRequestListTable.Component
        {...props}
        columns={columns}
        {...tableState}
        onChange={setTableState}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
      />
    );
  },

  FormAppointmentGroupToggle: (props) => {
    const [isPatient] = useUserContextSelector((x) => [x.hasRole('PATIENT')]);

    if (isPatient) {
      return null;
    }

    return <FormAppointmentGroupToggle.Component {...props} />;
  },

  FormVisitorReferenceIndividual: (props) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
    const [isPatient] = useUserContextSelector((x) => [x.hasRole('PATIENT')]);
    const [{ value }] = useField<VisitorReferenceFormValue>(props.name);
    const visitor = value?.visitor as PatientLookupOption | null;

    const visibleFields = props.visibleFields ?? ['referenceField', 'visitorField'];

    return (
      <>
        <FormVisitorReferenceIndividual.Component
          {...props}
          visibleFields={
            isPatient
              ? visibleFields.filter((field: 'referenceField' | 'visitorField') => field !== 'visitorField')
              : visibleFields
          }
        />
        <TextBox className="text-description" label={t('address')}>
          {visitor?.address ? formatters.address(visitor.address) : t('notApplicable')}
        </TextBox>
      </>
    );
  },

  FormAddAppointmentVisitor: (props) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
    const [field, , { setValue }] = useField<PatientLookupOption | null>(props.name);
    const { data: defaultVisitorLookup } = useGetPatientLookupQuery(
      { id: props.defaultVisitorId! },
      { skip: !props.defaultVisitorId },
    );
    const appliedDefaultVisitor = useRef(false);
    const defaultVisitor = defaultVisitorLookup ? mapPatientLookupOption(defaultVisitorLookup) : null;
    const visitor = field.value ?? defaultVisitor;

    useEffect(() => {
      if (appliedDefaultVisitor.current || !props.defaultVisitorId) {
        return;
      }

      if (!defaultVisitor) {
        return;
      }

      if (field.value?.value !== props.defaultVisitorId) {
        appliedDefaultVisitor.current = true;
        return;
      }

      setValue(defaultVisitor);
      appliedDefaultVisitor.current = true;
    }, [defaultVisitor, field.value, props.defaultVisitorId, setValue]);

    return (
      <>
        <FormAddAppointmentVisitor.Component {...props} />
        <TextBox className="text-description" label={t('address')}>
          {visitor?.address ? formatters.address(visitor.address) : t('notApplicable')}
        </TextBox>
      </>
    );
  },

  FormVisitorReferenceGroup: (props) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments' });
    const [isCareHomeStaff, userCareHomeId] = useUserContextSelector((x) => [
      x.hasRole('CARE_HOME_STAFF'),
      x.careHomeId,
    ]);

    const [field] = useField<VisitorReferenceFormValue[]>(props.name);
    const items = field.value ?? [];
    const firstVisitor = items[0]?.visitor ?? null;

    const careHomeId = isCareHomeStaff ? userCareHomeId : undefined;

    const { data: careHome } = useGetCatalogCareHomeQuery({ id: careHomeId! }, { skip: !careHomeId });
    const visitorCareHome = isCareHomeStaff ? careHome : firstVisitor?.careHome;

    if (props.showLocationSummary === false) {
      return <FormVisitorReferenceGroup.Component {...props} />;
    }

    return (
      <>
        <TextBox className="text-description" label={t('careHome')}>
          {visitorCareHome?.name ?? t('notApplicable')}
        </TextBox>

        <TextBox className="text-description" label={t('address')}>
          {visitorCareHome?.address ? formatters.address(visitorCareHome.address) : t('notApplicable')}
        </TextBox>

        <FormVisitorReferenceGroup.Component {...props} />
      </>
    );
  },

  FormAppointmentRequestRequestedPeriod: (props) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'appointments.requests' });
    const { values } = useFormikContext<AppointmentRequestFormValue>();
    const references = values.visitorRefs.map((item) => item.reference).filter(Boolean);
    const firstReference = references[0];
    const aggregatedDuration = references.reduce(
      (total, reference) => total + (reference?.durationInMinutes ?? 0),
      0,
    );
    const slotDurationMinutes = values.isGroup ? aggregatedDuration : firstReference?.durationInMinutes;

    if (props.readonly) {
      return values.requestedPeriod ? (
        <TextBox className="text-description" label={t('requestedPeriod')}>
          {`${formatters.datetime(values.requestedPeriod.start)} - ${formatters.datetime(
            values.requestedPeriod.end,
          )}`}
        </TextBox>
      ) : null;
    }

    return (
      <FormAppointmentRequestRequestedPeriod.Component
        {...props}
        extraSlotQueryArg={
          values.visitorRefs.some((vr) => !!vr.reference)
            ? {
                useActivePerformers: true,
                ...(slotDurationMinutes ? { slotDurationMinutes } : {}),
              }
            : null
        }
      />
    );
  },

  AppointmentRequestDetailsModalWidget: (props) => {
    return <AppointmentRequestDetailsModalWidget.Component {...props} readonly />;
  },

  AppointmentRequestDetailsWidget: (props) => {
    const [isPatient] = useUserContextSelector((x) => [x.hasRole('PATIENT')]);

    if (isPatient) {
      return <PatientPortalAppointmentRequestDetailsWidget {...props} />;
    }

    return <CustomAppointmentRequestDetailsWidget {...props} />;
  },

  NoAvailableAppointmentSlotsButton: (props) => {
    const [id, isPatient] = useUserContextSelector((x) => [x.id, x.hasRole('PATIENT')]);

    if (props.appointmentRequestDefaultValue?.visitorRefs.some((vr) => !!vr.reference && !!vr.visitor)) {
      return (
        <NoAvailableAppointmentSlotsButton.Component {...props} visitorId={isPatient ? id : undefined} />
      );
    }

    return null;
  },

  FormCareHome: (props) => {
    const {
      name,
      required,
      appointmentType,
      always: propsAlways,
      disabled,
      showDescription = true,
    } = props as {
      name: string;
      required?: boolean;
      appointmentType: AppointmentType | null;
      always?: CatalogCareHomeOption[];
      disabled?: boolean;
      showDescription?: boolean;
    };
    const { t } = useTranslation();
    const type = appointmentType ? APPOINTMENT_TYPE_CATALOG_CARE_HOME_TYPE_MAP[appointmentType] : null;
    const { event } = useEditAppointmentEventContext() ?? {};
    const optionSource = useCatalogCareHomeOptionSource({ type: type ?? undefined, skip: !type });
    const [, , { setValue }] = useField<string | null>(name);
    const always = useMemo(
      () => propsAlways ?? (event ? [mapCatalogCareHomeOption(event.careHome)] : []),
      [event, propsAlways],
    );
    const [option, setOption] = useState<CatalogCareHomeOption | null>(always[0] ?? null);

    useChangeEffect(() => {
      setValue(null);
      setOption(null);
    }, [appointmentType]);

    return (
      <>
        <Form.Item name={name} required={required}>
          {/* TODO: Tech Debt, Form.AsyncSelect doesn't react on option source change, might be fixed */}
          <Form.AsyncSelect
            key={type}
            optionSource={optionSource}
            always={always}
            disabled={disabled}
            onSelect={(_, selected) => setOption(selected as CatalogCareHomeOption)}
          />
        </Form.Item>
        {showDescription && (
          <TextBox className="text-description mb-4" label={t('address')}>
            {option?.address ? formatters.address(option.address) : t('notApplicable')}
          </TextBox>
        )}
      </>
    );
  },

  CancelAppointmentModal: (props) => {
    const [isPatient, isCareHomeStaff] = useUserContextSelector((x) => [
      x.hasRole('PATIENT'),
      x.hasRole('CARE_HOME_STAFF'),
    ]);

    if (isPatient || isCareHomeStaff) {
      return (
        <CancelAppointmentChatModalWidget
          visitorId={props.event.visitorId}
          appointmentStart={props.event.period.start}
          onClose={props.onClose}
        />
      );
    }

    return <CancelAppointmentModal.Component {...props} />;
  },

  AddAppointmentWidget: (props) => <CustomAddAppointmentWidget {...props} />,

  AppointmentSlotApplyForm: CustomAppointmentSlotApplyForm,
  AppointmentSlotApplyFormContent: CustomAppointmentSlotApplyFormContent,
  FormHostSearch: CustomFormHostSearch,

  AppointmentTypeValue: (props: { value: VisitorRefAppointmentTypeType }) => {
    return props.value?.name;
  },
};

export const ScheduleTheme = ({ children }: PropsWithChildren<object>) => {
  return (
    <Flexy value={SCHEDULE_THEME} mode="merge">
      {children}
    </Flexy>
  );
};
